import React, { useState, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { USER_ALPHA_JOIN, USER_ALPHA_JOIN_VERIFY } from '../../helper/gql';
import { maskEmail } from '../../helper/form';

type Step = 'email' | 'code' | 'success';

interface AlphaJoinState {
  step: Step;
  email: string;
  code: string;
  emailError: string;
  codeError: string;
  isLoading: boolean;
}

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const isValidCode = (code: string): boolean => {
  return /^\d{6}$/.test(code);
};

export const AlphaJoin: React.FC = () => {
  const history = useHistory();
  const [state, setState] = useState<AlphaJoinState>({
    step: 'email',
    email: '',
    code: '',
    emailError: '',
    codeError: '',
    isLoading: false
  });

  const [countdown, setCountdown] = useState(3);

  const [alphaJoin] = useMutation(USER_ALPHA_JOIN);
  const [alphaJoinVerify] = useMutation(USER_ALPHA_JOIN_VERIFY);

  useEffect(() => {
    if (state.step !== 'success') return;

    if (countdown <= 0) {
      history.push('/dashboard');
      return;
    }

    const timer = setInterval(() => {
      setCountdown(prev => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [state.step, countdown, history]);

  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setState(prev => ({ ...prev, email: value, emailError: '' }));
  }, []);

  const handleCodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setState(prev => ({ ...prev, code: value, codeError: '' }));
  }, []);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!state.email) {
      setState(prev => ({ ...prev, emailError: 'Email is required' }));
      return;
    }

    if (!isValidEmail(state.email)) {
      setState(prev => ({ ...prev, emailError: 'Please enter a valid email address' }));
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, emailError: '' }));

    try {
      const { data } = await alphaJoin({
        variables: { input: { email: state.email } }
      });

      if (data?.userAlphaJoin?.success) {
        setState(prev => ({ ...prev, step: 'code', isLoading: false }));
      } else {
        setState(prev => ({
          ...prev,
          emailError: data?.userAlphaJoin?.message || 'Failed to send verification code',
          isLoading: false
        }));
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        emailError: error instanceof Error ? error.message : 'An error occurred',
        isLoading: false
      }));
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!state.code) {
      setState(prev => ({ ...prev, codeError: 'Verification code is required' }));
      return;
    }

    if (!isValidCode(state.code)) {
      setState(prev => ({ ...prev, codeError: 'Please enter a valid 6-digit code' }));
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, codeError: '' }));

    try {
      const { data } = await alphaJoinVerify({
        variables: { input: { email: state.email, passcode: state.code } }
      });

      if (data?.userAlphaJoinVerify?.success) {
        if (data.userAlphaJoinVerify.token) {
          localStorage.setItem('authToken', data.userAlphaJoinVerify.token);
        }
        setState(prev => ({ ...prev, step: 'success', isLoading: false }));
      } else {
        setState(prev => ({
          ...prev,
          codeError: data?.userAlphaJoinVerify?.message || 'Verification failed',
          isLoading: false
        }));
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        codeError: error instanceof Error ? error.message : 'An error occurred',
        isLoading: false
      }));
    }
  };

  const getStepNumber = (): number => {
    switch (state.step) {
      case 'email': return 1;
      case 'code': return 2;
      case 'success': return 3;
      default: return 1;
    }
  };

  const renderEmailStep = () => (
    <div className="auth-step">
      <div className="step-header">
        <h2>Join the Alpha</h2>
        <p>Enter your email to get started</p>
      </div>
      <form onSubmit={handleEmailSubmit}>
        <div className="form-group">
          <label htmlFor="email">Email Address</label>
          <input
            id="email"
            type="email"
            value={state.email}
            onChange={handleEmailChange}
            placeholder="Enter your email"
            autoFocus
          />
        </div>
        {state.emailError && (
          <div className="error-message">{state.emailError}</div>
        )}
        <button type="submit" className="auth-button" disabled={state.isLoading}>
          {state.isLoading ? 'Sending...' : 'Continue'}
        </button>
      </form>
    </div>
  );

  const renderCodeStep = () => (
    <div className="auth-step">
      <div className="step-header">
        <h2>Verify Your Email</h2>
        <p>Enter the 6-digit code sent to {maskEmail(state.email)}</p>
      </div>
      <form onSubmit={handleCodeSubmit}>
        <div className="form-group">
          <label htmlFor="code">Verification Code</label>
          <input
            id="code"
            type="text"
            value={state.code}
            onChange={handleCodeChange}
            placeholder="Enter 6-digit code"
            maxLength={6}
            autoFocus
          />
        </div>
        {state.codeError && (
          <div className="error-message">{state.codeError}</div>
        )}
        <button type="submit" className="auth-button" disabled={state.isLoading}>
          {state.isLoading ? 'Verifying...' : 'Verify'}
        </button>
        <button
          type="button"
          onClick={() => setState(prev => ({ ...prev, step: 'email' }))}
          className="auth-button-secondary"
          disabled={state.isLoading}
        >
          Back
        </button>
      </form>
    </div>
  );

  const renderSuccessStep = () => (
    <div className="auth-step">
      <div className="step-header">
        <h2>Welcome to the Alpha!</h2>
        <p>Your account has been verified successfully.</p>
        <p>Redirecting to dashboard in {countdown} seconds...</p>
      </div>
    </div>
  );

  const renderStep = () => {
    switch (state.step) {
      case 'email': return renderEmailStep();
      case 'code': return renderCodeStep();
      case 'success': return renderSuccessStep();
      default: return renderEmailStep();
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="progress-bar">
          <div className="progress-indicator">
            Step {getStepNumber()} of 3
          </div>
          <div className="progress-line">
            <div
              className="progress-fill"
              style={{ width: `${(getStepNumber() / 3) * 100}%` }}
            />
          </div>
        </div>

        {renderStep()}

        <div className="auth-footer">
          <p><a href="/">← Back to Home</a></p>
        </div>
      </div>
    </div>
  );
};